import React, { useEffect, useReducer, useContext } from "react";
import "../../common/Insights.scss";
import OpenDrillDownView from "../../common/OpenDrillDownView";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice,
} from "@watermarkinsights/ripple-react";
import { sendRequest } from "../../../common/http";
import { AppContext, AppContextInterface } from "../../../contexts/AppContext";
import translate from "@/i18n/translate";
import { useIntl } from "react-intl";
import { openFlyout } from "../../../static/js/commons";
import ApiError from "@/features/common/elements/ApiError";
import { useFormatMessage } from "@/features/common/helper";

const COMPLETED = "completed";
const NOT_STARTED = "not_started";
const drilldownFilters = [
  {
    value: COMPLETED,
    name: translate("app.drilldown.completed"),
  },
  {
    value: NOT_STARTED,
    name: translate("app.drilldown.notStarted"),
  },
];
const popover = (amount: number, total: number) => {
  return `${amount}/${total}`;
};

interface Children {
  children: Children[];
  level: number;
  outcomes: number;
  name: string;
  show_stats: boolean;
  uuid: string;
  type: string;
  status: string;
}

interface Hierarchy {
  level: number;
  name: string;
  showStats: boolean;
  uuid: string;
  children: Children[];
}
interface ProgramProgressCardState {
  drilldownType: string;
  drilldownStatus: string;
  isDrilldownOpen: boolean;
  hierarchy: Hierarchy[];
  showTable: boolean;
  totalPrograms: number;
  programMissionComplete: number;
  programMissionIncomplete: number;
  programLearningOutcomes: number;
  programLearningOutcomesIncomplete: number;
  programSuccessOutcomes: number;
  programSuccessOutcomesIncomplete: number;
  programCurriculumMapsComplete: number;
  programCurriculumMapsIncomplete: number;
  iloProgramMappingsComplete: number;
  iloProgramMappingsIncomplete: number;
  apiError: boolean;
}

interface SetMissionOutcomeData {
  totalPrograms: number;
  programMissionComplete: number;
  programMissionIncomplete: number;
  programLearningOutcomes: number;
  programLearningOutcomesIncomplete: number;
  programSuccessOutcomes: number;
  programSuccessOutcomesIncomplete: number;
  programCurriculumMapsComplete: number;
  programCurriculumMapsIncomplete: number;
  iloProgramMappingsComplete: number;
  iloProgramMappingsIncomplete: number;
}

interface ProgramProgressCardAction {
  type: string;
  missionOutcomeData: SetMissionOutcomeData;
  segment: string;
  status: string;
  hierarchy: Hierarchy[];
  apiError: boolean;
}

const reducer = (
  state: ProgramProgressCardState,
  action: ProgramProgressCardAction
) => {
  switch (action.type) {
    case "setMissionOutcome":
      return {
        ...state,
        totalPrograms: action.missionOutcomeData.totalPrograms,
        programMissionComplete:
          action.missionOutcomeData.programMissionComplete,
        programMissionIncomplete:
          action.missionOutcomeData.programMissionIncomplete,
        programLearningOutcomes:
          action.missionOutcomeData.programLearningOutcomes,
        programLearningOutcomesIncomplete:
          action.missionOutcomeData.programLearningOutcomesIncomplete,
        programSuccessOutcomes:
          action.missionOutcomeData.programSuccessOutcomes,
        programSuccessOutcomesIncomplete:
          action.missionOutcomeData.programSuccessOutcomesIncomplete,
        programCurriculumMapsComplete:
          action.missionOutcomeData.programCurriculumMapsComplete,
        programCurriculumMapsIncomplete:
          action.missionOutcomeData.programCurriculumMapsIncomplete,
        iloProgramMappingsComplete:
          action.missionOutcomeData.iloProgramMappingsComplete,
        iloProgramMappingsIncomplete:
          action.missionOutcomeData.iloProgramMappingsIncomplete,
      };
    case "setDrilldownData":
      return {
        ...state,
        drilldownType: action.segment,
        drilldownStatus: action.status,
      };
    case "openDrilldown":
      return {
        ...state,
        isDrilldownOpen: true,
      };
    case "closeDrilldown":
      return {
        ...state,
        isDrilldownOpen: false,
        showTable: false,
        hierarchy: [],
      };
    case "setHierarchy":
      return {
        ...state,
        hierarchy: action.hierarchy,
        showTable: true,
      };
    case "setApiError":
      return { ...state, apiError: action.apiError };
    default:
      return state;
  }
};

export interface ProgramProgressCardProps {
  onTabLoad: (tab: string) => void;
  onComponentLoad: (type: string) => void;
}

export default function ProgramProgressCard({
  onTabLoad,
  onComponentLoad,
}: ProgramProgressCardProps) {
  const { outcomesTerminology, leads } =
    useContext<AppContextInterface>(AppContext);
  const formatMessage = useFormatMessage(); 
  const POPOVER_BUTTON_TEXT = useIntl().formatMessage({
    id: "app.programProgressCard.progressDrilldown",
  });
  const initialState = {
    drilldownType: "",
    drilldownStatus: "any",
    isDrilldownOpen: false,
    hierarchy: [],
    showTable: false,
    totalPrograms: 0,
    programMissionComplete: 0,
    programMissionIncomplete: 0,
    programLearningOutcomes: 0,
    programLearningOutcomesIncomplete: 0,
    programSuccessOutcomes: 0,
    programSuccessOutcomesIncomplete: 0,
    programCurriculumMapsComplete: 0,
    programCurriculumMapsIncomplete: 0,
    iloProgramMappingsComplete: 0,
    iloProgramMappingsIncomplete: 0,
    apiError: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const defaultAction = {
    type: "",
    apiError: false,
    missionOutcomeData: {
      totalPrograms: 0,
      programMissionComplete: 0,
      programMissionIncomplete: 0,
      programLearningOutcomes: 0,
      programLearningOutcomesIncomplete: 0,
      programSuccessOutcomes: 0,
      programSuccessOutcomesIncomplete: 0,
      programCurriculumMapsComplete: 0,
      programCurriculumMapsIncomplete: 0,
      iloProgramMappingsComplete: 0,
      iloProgramMappingsIncomplete: 0,
    },
    segment: "",
    status: "",
    hierarchy: [],
  };

  useEffect(() => {
    const applyResult = (result: SetMissionOutcomeData) => {
      dispatch({
        ...defaultAction,
        type: "setMissionOutcome",
        missionOutcomeData: result,
      });
      onTabLoad("programs");
      onComponentLoad("programs");
    };
    const onError = () => {
      dispatch({
        ...defaultAction,
        type: "setApiError",
        apiError: true,
      });
      onTabLoad("programs");
      onComponentLoad("programs");
    };

    sendRequest(
      { uri: "/mission_outcome/programs", camelize: true },
      applyResult,
      onError
    );
  }, []);

  const getDrilldownHierarchy = (segment: string) => {
    openDrilldown();
    const applyResult = (result: Hierarchy[]) => {
      dispatch({
        ...defaultAction,
        type: "setHierarchy",
        hierarchy: result,
      });
    };
    sendRequest(
      { uri: `/hierarchy?segment=${segment}`, camelize: true },
      applyResult
    );
  };

  const openDrilldownView = (segment: string, status: string) => {
    dispatch({
      ...defaultAction,
      type: "setDrilldownData",
      segment: segment,
      status: status,
    });
    getDrilldownHierarchy(segment);
  };
  const openDrilldown = () => {
    dispatch({
      ...defaultAction,
      type: "openDrilldown",
    });
  };
  const closeDrilldown = () => {
    dispatch({
      ...defaultAction,
      type: "closeDrilldown",
    });
  };

  const DD_SUB_HEADER = formatMessage(
    { id: "app.orgProgressCard.missionOutcome" },
    { outcome_singular: outcomesTerminology?.outcomes?.singular || "..." }
  );

  const programsWithMission = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithMission",
  });
  const completed = useIntl().formatMessage({
    id: "app.drilldown.completed",
  });
  const notStarted = useIntl().formatMessage({
    id: "app.drilldown.notStarted",
  });
  const added = useIntl().formatMessage({
    id: "app.drilldown.added",
  });
  const notAdded = useIntl().formatMessage({
    id: "app.drilldown.notAdded",
  });
  const created = useIntl().formatMessage({
    id: "app.drilldown.created",
  });

  const notCreated = useIntl().formatMessage({
    id: "app.drilldown.notCreated",
  });

  const mapped = useIntl().formatMessage({
    id: "app.drilldown.mapped",
  });

  const notMapped = useIntl().formatMessage({
    id: "app.drilldown.notMapped",
  });
  const missionStatement = useIntl().formatMessage({
    id: "app.orgProgressCard.missionStatement",
  });
  const programsWithoutMission = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithoutMission",
  });
  
  const learningOutcomes = formatMessage(
    { id: "app.programProgressCard.learningOutcomes" },
    { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
  );
  const successOutcomes = formatMessage(
    { id: "app.programProgressCard.successOutcomes" },
    { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
  );
  const ILOMapped = useIntl().formatMessage({
    id: "app.orgProgressCard.ILOMapped",
  });
  const curriculumMap = useIntl().formatMessage({
    id: "app.orgProgressCard.curriculumMap",
  });

  const programsWithLO = formatMessage(
    { id: "app.programProgressCard.programsWithLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural.toLowerCase() || "..." }
  );
  const programsWithoutLO = formatMessage(
    { id: "app.programProgressCard.programsWithoutLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural.toLowerCase() || "..." }
  );
  const programsWithSO = formatMessage(
    { id: "app.programProgressCard.programsWithSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural.toLowerCase() || "..." }
  );
  const programsWithoutSO = formatMessage(
    { id: "app.programProgressCard.programsWithoutSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural.toLowerCase() || "..." }
  );
  const programsWithCM = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithCM",
  });
  const programsWithoutCM = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithoutCM",
  });
  const programsWithILO = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithILO",
  });
  const programsWithoutILO = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithoutILO",
  });

  const totalPrograms = useIntl().formatMessage(
    { id: "app.programProgressCard.totalPrograms" },
    { totalPrograms: state.totalPrograms }
  );
  const programChart = () => {
    return (
      <div id="prog-charts">
        <WmProgressMonitor id="program-progress" bar-labels-width="5rem">
          <WmProgressIndicator
            label={missionStatement}
            onWmProgressIndicatorButtonTriggered={(e) => {
              openDrilldownView(
                "program_mission",
                e.detail.legend === added ? COMPLETED : NOT_STARTED
              );
            }}
          >
            <WmProgressSlice
              legend={added}
              amount={state.programMissionComplete.toString()}
              popoverTitle={popover(
                state.programMissionComplete,
                state.totalPrograms
              )}
              popoverText={programsWithMission}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
            <WmProgressSlice
              legend={notAdded}
              amount={state.programMissionIncomplete.toString()}
              popoverTitle={popover(
                state.programMissionIncomplete,
                state.totalPrograms
              )}
              popoverText={programsWithoutMission}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
          </WmProgressIndicator>
          <WmProgressIndicator
            label={learningOutcomes}
            onWmProgressIndicatorButtonTriggered={(e) => {
              openDrilldownView(
                "program_learning_outcomes",
                e.detail.legend === created ? COMPLETED : NOT_STARTED
              );
            }}
          >
            <WmProgressSlice
              legend={created}
              amount={state.programLearningOutcomes.toString()}
              popoverTitle={popover(
                state.programLearningOutcomes,
                state.totalPrograms
              )}
              popoverText={programsWithLO}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
            <WmProgressSlice
              legend={notCreated}
              popoverTitle={popover(
                state.programLearningOutcomesIncomplete,
                state.totalPrograms
              )}
              popoverText={programsWithoutLO}
              amount={state.programLearningOutcomesIncomplete.toString()}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
          </WmProgressIndicator>
          <WmProgressIndicator
            label={successOutcomes}
            onWmProgressIndicatorButtonTriggered={(e) => {
              openDrilldownView(
                "program_success_outcomes",
                e.detail.legend === created ? COMPLETED : NOT_STARTED
              );
            }}
          >
            <WmProgressSlice
              legend={created}
              amount={state.programSuccessOutcomes.toString()}
              popoverTitle={popover(
                state.programSuccessOutcomes,
                state.totalPrograms
              )}
              popoverText={programsWithSO}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
            <WmProgressSlice
              legend={notCreated}
              amount={state.programSuccessOutcomesIncomplete.toString()}
              popoverTitle={popover(
                state.programSuccessOutcomesIncomplete,
                state.totalPrograms
              )}
              popoverText={programsWithoutSO}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
          </WmProgressIndicator>
          <WmProgressIndicator
            label={curriculumMap}
            onWmProgressIndicatorButtonTriggered={(e) => {
              openDrilldownView(
                "program_cmap",
                e.detail.legend === mapped ? COMPLETED : NOT_STARTED
              );
            }}
          >
            <WmProgressSlice
              legend={mapped}
              amount={state.programCurriculumMapsComplete.toString()}
              popoverTitle={popover(
                state.programCurriculumMapsComplete,
                state.totalPrograms
              )}
              popoverText={programsWithCM}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
            <WmProgressSlice
              legend={notMapped}
              amount={state.programCurriculumMapsIncomplete.toString()}
              popoverTitle={popover(
                state.programCurriculumMapsIncomplete,
                state.totalPrograms
              )}
              popoverText={programsWithoutCM}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
          </WmProgressIndicator>
          <WmProgressIndicator
            label={ILOMapped}
            onWmProgressIndicatorButtonTriggered={(e) => {
              openDrilldownView(
                "program_ilo_mapping",
                e.detail.legend === mapped ? COMPLETED : NOT_STARTED
              );
            }}
          >
            <WmProgressSlice
              legend={mapped}
              amount={state.iloProgramMappingsComplete.toString()}
              popoverTitle={popover(
                state.iloProgramMappingsComplete,
                state.totalPrograms
              )}
              popoverText={programsWithILO}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
            <WmProgressSlice
              legend={notMapped}
              amount={state.iloProgramMappingsIncomplete.toString()}
              popoverTitle={popover(
                state.iloProgramMappingsIncomplete,
                state.totalPrograms
              )}
              popoverText={programsWithoutILO}
              popoverButtonText={POPOVER_BUTTON_TEXT}
            />
          </WmProgressIndicator>
        </WmProgressMonitor>
      </div>
    );
  };
  return (
    <React.Fragment>
      {state.apiError && <ApiError containerClasses="api-error" />}

      {!state.apiError && (
        <div>
          <h4 className="title">
            {translate("app.programProgressCard.programProgressDefinition")}
          </h4>
          <p className="prog totals">
            {translate("app.programProgressCard.totalPrograms", {
              totalPrograms: state.totalPrograms,
            })}
          </p>
          {programChart()}
        </div>
      )}

      <OpenDrillDownView
        setIsDrilldownOpen={closeDrilldown}
        drilldownType={state.drilldownType}
        drilldownStatus={state.drilldownStatus}
        subHeader={DD_SUB_HEADER}
        total={totalPrograms}
        showTable={state.showTable}
        completeHierarchy={state.hierarchy}
        filters={drilldownFilters}
        leads={leads}
        isVisible={state.isDrilldownOpen}
        open={state.isDrilldownOpen}
        id="program-progress-drill-down-view"
      ></OpenDrillDownView>
    </React.Fragment>
  );
}
